import { factor } from '@fameacc/money';
import { z } from 'zod';
import { dashboardTrendFactorSchema, dashboardZeitraumTypSchema, } from './berichte.js';
import { centAmount, isoDate } from './posting.js';
/**
 * Die Basis als `readonly string[]` und nicht als Literalvereinigung — und
 * das ist keine Nachlässigkeit, sondern der Grund, warum diese Datei beide
 * Fassungen des Dashboard-Schemas verträgt.
 *
 * Solange dort `gesamt` fehlt, hielte TypeScript den Vergleich
 * `wert !== 'gesamt'` für unbeabsichtigt (`TS2367`: keine Überschneidung)
 * und die Übersetzung bräche. Sobald der Zweig `w-uebersichtgesamt` gelandet
 * ist, wäre derselbe Vergleich richtig typisiert. Eine Zeile, die nur in
 * genau einer der beiden Welten übersetzt, wäre ein eingebauter
 * Zusammenführungskonflikt — die Verbreiterung auf `string` macht sie in
 * beiden richtig, und der Typ der ERGEBNISLISTE bleibt trotzdem eng.
 */
const ZEITRAUM_BASIS = dashboardZeitraumTypSchema.options;
const ZEITRAUM_WERTE = [
    'gesamt',
    ...ZEITRAUM_BASIS.filter((wert) => wert !== 'gesamt'),
];
export const auswertungZeitraumTypSchema = z.enum(ZEITRAUM_WERTE);
/**
 * Die Anzeigereihenfolge des Zeitraumschalters — Woche · Monat · Quartal ·
 * Jahr · Gesamt (Spec 3.2, Punkt 1).
 *
 * **Sie ist eine Sicht auf die Aufzählung und keine zweite Liste:** Was hier
 * steht, muss dort vorkommen, und `E-1139` hält beide Mengen gegeneinander.
 * Ein Zeitraum, der im Dashboard-Schema dazukäme und hier nicht genannt
 * wäre, fiele dort auf — nicht erst, wenn ihn jemand im Schalter vermisst.
 */
export const AUSWERTUNG_ZEITRAEUME_ANZEIGE = [
    'woche',
    'monat',
    'quartal',
    'jahr',
    'gesamt',
];
/** Der Zeitraum ohne Angabe. Dieselbe Wahl wie beim Dashboard-Endpunkt. */
export const AUSWERTUNG_ZEITRAUM_VORGABE = 'monat';
/**
 * Die Länge der Zeitreihe unter dem Chart — 12, 24, 36 Monate oder gesamt,
 * Vorgabe **24** (`AW-1`, Spec 3.2).
 *
 * **Zeichenketten und keine Zahlen**, weil `gesamt` keine Zahl ist und eine
 * Aufzählung aus vier Werten nicht zur Hälfte numerisch sein soll. Der
 * Dienst übersetzt sie in Monatsspannen
 * (`apps/api/src/auswertungen/zeitraum.ts`).
 */
export const auswertungReihenlaengeSchema = z.enum(['12', '24', '36', 'gesamt']);
/** Die Reihenlänge ohne Angabe — 24 Monate (`AW-1`). */
export const AUSWERTUNG_REIHE_VORGABE = '24';
/**
 * Der aufgelöste Zeitraum: gewählter Typ **und** die Datumsspanne, die
 * daraus wurde.
 *
 * **Beides und nicht nur eines.** Der Typ allein liesse den Leser die Spanne
 * nachrechnen (und dabei die Frage „welcher Montag" oder „welcher Anfang bei
 * gesamt" falsch beantworten); die Spanne allein verlöre die Information,
 * welchen Schalter der Anwender gedrückt hat.
 */
export const auswertungZeitraumSchema = z
    .object({
    typ: auswertungZeitraumTypSchema,
    von: isoDate,
    bis: isoDate,
})
    .strict();
/**
 * Die Grenzen einer Auswertung, als **Sätze** und nicht als Schlüssel
 * (`D-9`).
 *
 * **Nichtleer, und das ist die eigentliche Zusicherung** (`E-1138`): Es gibt
 * keine Auswertung dieses Bestandes ohne Grenze — das Journal beginnt 2026,
 * die Belegschicht 2022, ein abgeleitetes Steuerszenario stammt aus der Lage
 * des Empfängers und nicht aus dem gedruckten Beleg. Eine leere Liste wäre
 * die Behauptung, es gäbe nichts zu sagen; sie ist deshalb kein zulässiger
 * Wert und keine Bequemlichkeit für den ersten Endpunkt.
 *
 * Die Sätze werden in Spec Abschnitt 4 **vergeben** und stehen dort nicht
 * ein zweites Mal — was der Endpunkt ausliefert, ist derselbe Wortlaut.
 */
export const auswertungGrenzenSchema = z
    .array(z.string().min(1, 'Eine Grenze ohne Text ist keine Grenze'))
    .min(1, 'Jede Auswertung nennt mindestens eine Grenze');
/**
 * Die drei Felder, die jede Antwort dieses Moduls trägt — als Gestaltsatz,
 * damit eine Antwort sie mit einem Spread übernimmt.
 *
 * **Ein Gestaltsatz und kein `.merge()` auf einem fertigen Objektschema.**
 * Wer eine Antwort baut, schreibt
 * `z.object({ ...auswertungHuelleGestalt, eigenes: … }).strict()` — eine
 * Zeile, und das Weglassen eines Hüllenfeldes ist ein Übersetzungsfehler
 * statt eines vergessenen Aufrufs.
 *
 * **`stichtag` trägt eine Uhrzeit und nicht nur einen Kalendertag.** Der
 * Name ist der aus `D-10` (Vorbild `StartkennzahlenBericht.stichtag`), die
 * FORM ist die des Dashboard-Feldes `stand`: Die Seite zeigt „Stand:
 * TT.MM.JJJJ, HH:MM Uhr" (Spec 3.2, Punkt 4), und ein reines Datum kann die
 * Stunde nicht hergeben. Ein Feld, das weniger weiss als die Anzeige
 * behauptet, wäre genau die zweite Wahrheit, gegen die diese Ära gebaut ist.
 */
export const auswertungHuelleGestalt = {
    stichtag: z.string().datetime({ offset: true }),
    zeitraum: auswertungZeitraumSchema,
    grenzen: auswertungGrenzenSchema,
};
/** Die Hülle für sich — die kleinste gültige Antwort dieses Moduls. */
export const auswertungHuelleSchema = z.object({ ...auswertungHuelleGestalt }).strict();
/** Eine Unterseite der Bereichsseite: Schlüssel für die Adresse, Titel für den Reiter. */
export const auswertungUnterseiteSchema = z
    .object({
    schluessel: z.string().min(1),
    titel: z.string().min(1),
    /**
     * Die Zeitschicht, aus der diese Unterseite liest — als Satz, nicht als
     * Schlüssel (Spec 3.1, Spalte „Zeitschicht"). Sie steht schon auf der
     * Bereichsseite und nicht erst auf der Unterseite: Wer wählt, welche
     * Auswertung er öffnet, entscheidet damit auch, welche Grenze er lesen
     * wird.
     */
    zeitschicht: z.string().min(1),
})
    .strict();
/**
 * Die Antwort der Bereichsseite `GET /api/v1/auswertungen` — die Hülle plus
 * das Verzeichnis der Unterseiten.
 *
 * **Sie ist nicht die Summe der sieben Unterseiten**, und das ist Absicht:
 * Eine Bereichsseite, die alle sieben Auswertungen zugleich rechnet, wäre
 * die teuerste Anfrage dieses Systems und zeigte doch nur, was jede
 * Unterseite besser zeigt. Was sie liefert, ist der Beweis der Fähigkeit
 * (`auswertung:read`), der Stichtag für die Fussleiste und die Liste dessen,
 * was es überhaupt gibt.
 */
export const auswertungUeberblickSchema = z
    .object({
    ...auswertungHuelleGestalt,
    unterseiten: z.array(auswertungUnterseiteSchema).min(1),
})
    .strict();
// ===========================================================================
// UMSATZ — Task 3 [AW-umsatz]
// ===========================================================================
/**
 * Ein Verhältnis als `Factor` — `0.05` heisst plus 5 %, `-0.12` minus 12 %,
 * `0.42` ein Anteil von 42 % (`D-3`).
 *
 * **Nicht `number`, und das ist die erste Entscheidung dieser Ära.** Der
 * Gedanke „Prozentwerte sind Verhältnisse und dürfen `number` sein" wäre in
 * diesem Haus die erste `number`-Geldableitung überhaupt; der
 * Dashboard-Vertrag hat die Frage bereits beantwortet („Eine
 * Prozent-Differenz ist eine Geldableitung und unterliegt Hausregel 1 genauso
 * wie ein Betrag selbst"), und `money/no-float-money` greift auch in Tests.
 *
 * **Derselbe Baustein wie `dashboardTrendFactorSchema` in `berichte.ts`, und
 * er steht hier trotzdem noch einmal.** Jener ist dort nicht exportiert, und
 * ein Export quer durch zwei Vertragsmodule verbände zwei Antwortgestalten
 * miteinander, die nichts teilen ausser der Schreibweise einer Zahl. Was
 * geteilt werden MUSS, ist die RECHNUNG — und die steht genau einmal, als
 * `factor(div(sub(a, b), factor(b)))` im Dienst.
 *
 * Ohne die Bereichsgrenze `< 1` von `taxRateFactor`: Ein Wachstum darf
 * negativ sein und über `1` liegen, beides kommt vor.
 */
export const auswertungFaktorSchema = z.string().transform((wert, ctx) => {
    try {
        return factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Kein gültiger Faktor' });
        return z.NEVER;
    }
});
/**
 * Die drei Szenariogruppen aus Konzept 8, wie sie die Sicht
 * `auswertung_beleg_monat` bildet — nach der Lage des Empfängers und nicht
 * nach dem Steuersatz.
 *
 * Die sechs Steuerszenarien des Belegwesens fallen in genau eine davon; die
 * Zuordnung steht typvollständig in `apps/api/src/auswertungen/steuer.query.ts`
 * und ist dort so getypt, dass ein siebtes Szenario den Übersetzer rot macht
 * statt die Summe still zu verkürzen.
 *
 * **Der Name allein, und deshalb `...Name`:** Eine Szenariogruppe MIT ihren
 * Zahlen ist das gleichnamige Schema der Steuerauswertung weiter unten.
 */
export const auswertungSzenariogruppeNameSchema = z.enum(['inland', 'eu', 'drittland']);
/**
 * Die Summen eines Ausschnitts der Belegschicht — beide Klassen zusammen und
 * die gebuchte für sich.
 *
 * **Drei Beträge und nicht einer**, weil Netto, Steuer und Brutto drei
 * verschiedene Fragen beantworten und „Brutto und Netto sind zwei Reihen und
 * nie eine" (Spec 4.1). Welche gezeigt wird, entscheidet die Seite; welche es
 * gibt, entscheidet der Vertrag.
 *
 * **`nettoGebucht` und `nettoArchiv` sind der Kern der Abstimmbarkeit**
 * (`D-30`): `netto = nettoGebucht + nettoArchiv` auf den Cent, und
 * `nettoGebucht` ist auf den Cent das, was `summeUmsatzUndGutschrift()` über
 * dieselbe Spanne liefert (`E-1142`). Ein Leser, der nur `netto` bekäme,
 * könnte die Zahl gegen nichts halten — und eine Zahl, die man nur auf einer
 * Seite sehen kann, ist in einem GoBD-System keine Kennzahl.
 *
 * **`belege` ist eine ANZAHL und deshalb `number`.** Hausregel 1 gilt für
 * Beträge; eine Stückzahl ist keiner. Sie zählt die Gutschrift als einen
 * Beleg und dreht dort kein Vorzeichen: Wer „wie viele Rechnungen" fragt,
 * meint nicht „minus eine".
 */
export const auswertungUmsatzSummenSchema = z
    .object({
    netto: centAmount,
    steuer: centAmount,
    brutto: centAmount,
    nettoGebucht: centAmount,
    nettoArchiv: centAmount,
    belege: z.number().int().nonnegative(),
})
    .strict();
/**
 * Ein Monat der Umsatzreihe.
 *
 * **`anlaufjahr` markiert, statt zu glätten oder wegzulassen** (`D-13`). 2022
 * (217 Belege) und 2023 (947) beschreiben nichts, was sich wiederholen lässt;
 * +336 % zwischen ihnen ist richtig gerechnet und als Wachstumsaussage
 * wertlos. Weglassen wäre eine zweite Wahrheit, Glätten eine erfundene —
 * Markieren ist die dritte und einzige ehrliche Möglichkeit.
 *
 * **`wachstumVormonat` und `wachstumVorjahresmonat` sind `null`, wenn die
 * Vergleichsperiode exakt null war** (`D-14`, `E-1145`) — nie `0`, nie `1`,
 * nie ein Gedankenstrich, den der Vertrag als Zahl ausgäbe. Die Regel steht
 * schon in `trend()` (`dashboard-kennzahlen.report.ts`) und wird hier nur
 * ausgeweitet.
 *
 * **`vorjahresmonatAnlaufjahr` ist eine Aussage über den VERGLEICHSMONAT und
 * nicht über diesen.** Ein Wachstum von 2024 gegen 2023 ist gegen ein
 * Anlaufjahr gerechnet; der Wert bleibt sichtbar und trägt die Marke. Ohne
 * dieses zweite Feld müsste die Seite den Vergleichsmonat selbst suchen — und
 * für die ersten zwölf Monate der Reihe fände sie ihn nicht.
 */
export const auswertungUmsatzMonatSchema = z
    .object({
    /** `YYYY-MM`. Der Schlüssel der Reihe und die Rubrik im Chart. */
    monat: z.string().regex(/^\d{4}-\d{2}$/, 'Monat als YYYY-MM'),
    von: isoDate,
    bis: isoDate,
    summen: auswertungUmsatzSummenSchema,
    anlaufjahr: z.boolean(),
    wachstumVormonat: auswertungFaktorSchema.nullable(),
    wachstumVorjahresmonat: auswertungFaktorSchema.nullable(),
    vorjahresmonatAnlaufjahr: z.boolean(),
})
    .strict();
/**
 * Eine Szenariogruppe im gewählten Zeitraum.
 *
 * **`anteil` ist `null` ohne Gesamtumsatz** (`D-14`) und nicht `0`: Ein
 * Anteil an nichts ist kein Anteil. Die Summe der drei `netto` ist der
 * Gesamtumsatz desselben Zeitraums, auf den Cent (`E-1147`) — die erste
 * Abstimmung dieser Ära und die Vorlage für alle weiteren.
 */
export const auswertungUmsatzSzenarioSchema = z
    .object({
    gruppe: auswertungSzenariogruppeNameSchema,
    netto: centAmount,
    brutto: centAmount,
    belege: z.number().int().nonnegative(),
    anteil: auswertungFaktorSchema.nullable(),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/umsatz`.
 *
 * **Die Monatsreihe und der Zeitraum sind zwei verschiedene Dinge, und das
 * ist Absicht.** `zeitraum` (aus der Hülle) ist der Ausschnitt, für den die
 * Kennzahlkarten und die Szenarioaufschlüsselung gelten; `monate` ist die
 * Zeitreihe unter dem Chart, deren Länge der Anwender getrennt wählt (12 / 24
 * / 36 / gesamt, Vorgabe 24, `AW-1`). Ein Vertrag, der beides in ein Feld
 * legte, könnte „Umsatz dieses Monats" und „die letzten 24 Monate" nicht
 * gleichzeitig sagen.
 *
 * **`journalbeginn` steht in der Antwort und nicht in der Oberfläche**
 * (`D-12`): Die Trennlinie „Journalbeginn" gehört ins Bild, und die Seite
 * soll das Datum nicht kennen müssen. Auf DIESER Seite ist die Umsatzreihe
 * durchgehend belegbasiert — die Marke trägt trotzdem, weil sich die
 * Belegklassen genau dort trennen: vor 2026 Archivklasse, ab 2026 gebucht.
 */
export const auswertungUmsatzSchema = z
    .object({
    ...auswertungHuelleGestalt,
    reihe: auswertungReihenlaengeSchema,
    monate: z.array(auswertungUmsatzMonatSchema),
    /** Die Summen des gewählten Zeitraums — nicht die der Reihe. */
    zeitraumSummen: auswertungUmsatzSummenSchema,
    /**
     * Die unmittelbar vorangehende, gleich lange Periode — `null` beim
     * Zeitraum `gesamt`.
     *
     * **Nullbar und nicht leer gerechnet.** Vor `gesamt` liegt nichts: Die
     * Belegschicht beginnt am 1. Januar 2022, und eine „Vorperiode" davor
     * wäre eine Spanne, in der dieses Haus keine Rechnung geschrieben hat.
     * Eine erfundene leere Spanne stünde als Datum auf der Seite und sähe aus
     * wie eine Messung.
     */
    vorperiode: z.object({ von: isoDate, bis: isoDate }).strict().nullable(),
    /** `null`, wenn die Vorperiode exakt null war oder es keine gibt (`D-14`). */
    wachstumVorperiode: auswertungFaktorSchema.nullable(),
    szenarien: z.array(auswertungUmsatzSzenarioSchema),
    /** Netto der Belegart `recurring_invoice` im Zeitraum. Erst ab 2026 von null verschieden. */
    nettoWiederkehrend: centAmount,
    /** Anteil des wiederkehrenden am gesamten Netto. `null` ohne Umsatz (`D-14`). */
    anteilWiederkehrend: auswertungFaktorSchema.nullable(),
    /** Der Tag, ab dem dieses System bucht — `2026-01-01`. */
    journalbeginn: isoDate,
    /** Die Kalenderjahre, die als Anlaufjahre markiert werden (`D-13`) — aus dem Bestand abgeleitet, leer ohne Belege (`E-1972`). */
    anlaufjahre: z.array(z.number().int()),
})
    .strict();
// --- Kunden [AW-kunden] -----------------------------------------------------
/**
 * Ein Verhältnis als `Factor` — `0.42` heisst zweiundvierzig Prozent
 * (`D-3`).
 *
 * **Kein `number`, und das ist keine Formfrage.** Der Dashboard-Vertrag hat
 * sie schon einmal beantwortet: „Eine Prozent-Differenz ist eine
 * Geldableitung und unterliegt Hausregel 1 genauso wie ein Betrag selbst."
 * Ein `number`-Anteil wäre die erste `number`-Geldableitung dieses Hauses,
 * und `money/no-float-money` greift auch in Tests.
 *
 * **Eigenes Schema neben `dashboardTrendFactorSchema` und nicht dessen
 * Wiederverwendung**, obwohl beide dieselbe Form haben: Der Trend ist eine
 * DIFFERENZ gegen eine Vorperiode, der Anteil ein TEIL eines Ganzen. Sie
 * gleichen sich heute und dürfen sich morgen unterschiedlich entwickeln;
 * `dashboardTrendFactorSchema` ist zudem in `berichte.ts` nicht exportiert,
 * und es dafür zu exportieren hiesse, die zwei Bedeutungen aneinander zu
 * binden.
 *
 * **Ohne Bereichsgrenze, und das ist kein Versehen.** Ein Anteil über eins
 * ist in diesem Bestand ein mögliches, ehrliches Ergebnis: Trägt ein Kunde
 * ausserhalb der Rangliste in Summe eine Gutschrift, ist der Gesamtumsatz
 * kleiner als die Summe der grössten fünf, und ihr Anteil liegt über hundert
 * Prozent. Diese Zahl ist unbequem und richtig; sie abzuweisen hiesse, einen
 * Bestand für einen Rechenfehler zu halten. Der Fall ohne Bezugsgrösse
 * (Umsatz null oder negativ) wird dagegen gar nicht erst als Anteil geliefert
 * — dort steht `null` und ein Satz (`D-14`).
 */
// Der Anteil eines Kunden am Umsatz ist derselbe `Factor` wie jedes andere
// Verhältnis dieses Moduls: `auswertungFaktorSchema` weiter oben. Ein zweiter
// Name für dieselbe Form wäre zwei Wahrheiten — der Name `auswertungAnteilSchema`
// trägt in dieser Datei den ANTEIL EINER SERIE (`AW-zahlwege`, unten).
/**
 * Ein Monat der Kundenreihe: wie viele Kunden in ihm ZUM ERSTEN MAL einen
 * wertbehafteten Beleg trugen, und wie viele in ihm als aktiv gelten.
 *
 * **`neukunden` kommt aus `auswertung_kunde_erstbeleg` und nie aus
 * `customer.created_at`** (`D-17`). Bei 1.511 übernommenen Kunden ist
 * `created_at` das Importdatum und für praktisch alle derselbe Tag.
 *
 * **`aktiveKunden` ist eine DEFINITION und keine Messung:** Kunden mit
 * mindestens einem Beleg im gleitenden Fenster, dessen Länge die Antwort in
 * `aktivFensterMonate` mitliefert. Ein Kunde ohne Rechnung ist nicht
 * gekündigt — dieses System kennt keinen Kündigungszustand für Kunden.
 *
 * **`anlaufjahr` markiert 2022 und 2023** (`D-13`). 217 gegen 947 Belege sind
 * +336 % und beschreiben nichts Wiederholbares. Weglassen wäre eine zweite
 * Wahrheit, Glätten eine erfundene; Markieren ist die dritte und einzige
 * ehrliche Möglichkeit.
 */
export const auswertungKundenMonatSchema = z
    .object({
    /** Der Kalendermonat als `JJJJ-MM` — die Rubrik im Bild. */
    monat: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Erwartet JJJJ-MM'),
    von: isoDate,
    bis: isoDate,
    neukunden: z.number().int().min(0),
    aktiveKunden: z.number().int().min(0),
    anlaufjahr: z.boolean(),
})
    .strict();
/**
 * Eine Zeile der Kundenrangliste.
 *
 * **`kundennummer` und `name` kommen aus dem KUNDENSTAMM und nicht aus dem
 * Beleg** — anders als das Land (`auswertungLandSchema`). Der Grund ist die
 * Frage: Wer wie viel Umsatz getragen hat, liest man unter dem HEUTIGEN
 * Namen des Kunden, sonst stünde derselbe Kunde nach einer Umfirmierung
 * zweimal in der Liste. Beim Land ist es umgekehrt — dort verschöbe der
 * heutige Wert historischen Umsatz.
 */
export const auswertungKundenZeileSchema = z
    .object({
    customerId: z.string().uuid(),
    kundennummer: z.string().min(1),
    name: z.string().min(1),
    netto: centAmount,
    belege: z.number().int().min(0),
})
    .strict();
/**
 * Der abgeschnittene Rest der Rangliste — **benannt und nicht weggelassen**.
 *
 * Eine abgeschnittene Liste ohne benannten Rest ist eine Lüge über die
 * Summe; dieselbe Regel, die der Dashboard-Vertrag mit
 * `rest`/`restAnzahlKonten` schon führt. Erst `topKunden` plus `rest` ergibt
 * `umsatzGesamt` (`E-1152`).
 */
export const auswertungKundenRestSchema = z
    .object({
    netto: centAmount,
    anzahlKunden: z.number().int().min(0),
})
    .strict();
/**
 * Die Konzentration — **eine Risikoaussage und keine Erfolgsaussage**, und
 * die Beschriftung sagt das.
 *
 * **Ohne Umsatz im Zeitraum ist sie kein Wert, sondern ein Satz** (`D-14`,
 * `E-1154`): `anteil` ist dann `null`, nie `0`, nie `1` und nie ein Strich.
 * Eine Quote gegen null Bezugsgrösse behauptet etwas, das nicht stimmt.
 * `satz` steht in BEIDEN Fällen und trägt entweder die Risikoaussage oder
 * den Grund für den fehlenden Wert — die Seite muss deshalb keinen Text
 * erfinden.
 */
export const auswertungKonzentrationSchema = z
    .object({
    topAnzahl: z.number().int().min(1),
    anteil: auswertungFaktorSchema.nullable(),
    satz: z.string().min(1),
})
    .strict();
/**
 * Umsatz je Land — **das Land des BELEGS** (`E-1153`).
 *
 * `document.billing_country_code` ist am Beleg eingefroren.
 * `customer.country_code` ist der heutige Wohnsitz; wer ihn läse, verschöbe
 * historischen Umsatz, sobald ein Kunde umzieht — und niemand merkte es,
 * weil beide Zahlen für sich stimmen.
 */
export const auswertungLandSchema = z
    .object({
    code: z.string().length(2),
    netto: centAmount,
    belege: z.number().int().min(0),
})
    .strict();
/**
 * Abo-Abgänge — **zwei absolute Zahlen und keine Quote** (`D-18`,
 * `E-1155`).
 *
 * Zwanzig Verträge sind zu wenig für einen Prozentwert: Jede Kündigung
 * bewegt eine Quote um fünf Punkte, und eine Zahl, die so springt, misst das
 * Rauschen. Die Abgänge stehen deshalb **neben** dem Bestand, damit der
 * Leser das Verhältnis selbst sieht — und dieses Schema trägt kein Feld, in
 * das eine Quote passte.
 */
export const auswertungAboAbgaengeSchema = z
    .object({
    bestandAktiv: z.number().int().min(0),
    abgaengeImZeitraum: z.number().int().min(0),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/kunden`.
 *
 * Sie trägt die Hülle (`stichtag`, `zeitraum`, `grenzen`) wie jede Antwort
 * dieses Moduls und darüber hinaus vier Bilder: die Neukunden- und
 * Bestandsreihe, die Rangliste samt Konzentration, die Länderverteilung und
 * die Abo-Abgänge.
 *
 * **`umsatzGesamt` steht ausdrücklich da**, obwohl es die Summe der Zeilen
 * ist: Es ist der Bezugswert der Konzentration und der Gegenpart der
 * Abstimmung (`E-1152`). Eine Seite, die ihn selbst addierte, addierte ihn
 * über gerundete Anzeigewerte.
 */
export const auswertungKundenSchema = z
    .object({
    ...auswertungHuelleGestalt,
    /** Die gewählte Länge der Zeitreihe (`12`/`24`/`36`/`gesamt`). */
    reihe: auswertungReihenlaengeSchema,
    /**
     * Die Länge des gleitenden Fensters für „aktiv", in Monaten. Sie steht
     * in der Antwort und nicht nur im Code, weil sie am Bild stehen muss:
     * „Aktiv" ist eine Definition, und eine Definition ohne genannte
     * Grössenordnung ist eine Behauptung.
     */
    aktivFensterMonate: z.number().int().min(1),
    monate: z.array(auswertungKundenMonatSchema),
    topKunden: z.array(auswertungKundenZeileSchema),
    rest: auswertungKundenRestSchema,
    umsatzGesamt: centAmount,
    /** Kunden mit mindestens einem wertbehafteten Beleg im Zeitraum. */
    kundenGesamt: z.number().int().min(0),
    konzentration: auswertungKonzentrationSchema,
    laender: z.array(auswertungLandSchema),
    aboAbgaenge: auswertungAboAbgaengeSchema,
})
    .strict();
// --- Wiederkehrender Umsatz [AW-abo] ----------------------------------------
/**
 * Ein aktiver Abo-Vertrag in der Auswertung des wiederkehrenden Umsatzes.
 *
 * **`nettoJePeriode` und `nettoJeMonat` stehen beide da**, und das ist keine
 * Redundanz: Das erste ist der Betrag, den der Vertrag bei jeder Fakturierung
 * trägt — die Zahl, die auf der Rechnung steht. Das zweite ist dieselbe Zahl
 * auf den Monat normiert (`D-19`), und nur sie darf man über Verträge mit
 * verschiedenen Takten hinweg addieren. Wer nur eines von beiden lieferte,
 * zwänge den Leser zu einer Division — und eine Division im Browser ist genau
 * das, was `D-3` verhindert.
 */
export const auswertungAboVertragSchema = z
    .object({
    id: z.string().uuid(),
    bezeichnung: z.string().min(1),
    kundennummer: z.string().min(1),
    kundeName: z.string().min(1),
    intervallMonate: z.number().int().min(1),
    nettoJePeriode: centAmount,
    nettoJeMonat: centAmount,
    naechsterStichtag: isoDate,
})
    .strict();
/**
 * Ein Monat der Vorschau: wie viele Aborechnungen in ihm planmässig entstehen
 * und über welchen Nettobetrag zusammen.
 *
 * **Gezählt werden nur Stichtage AB HEUTE** (`E-1150`). Ein Stichtag, der
 * vergangen ist, ohne dass eine Rechnung entstand, ist **Rückstand** und keine
 * Planung: Der Generator überspringt ihn, und wer ihn mitzählte, plante eine
 * Rechnung, die nie entsteht. Die Zahl der betroffenen Verträge steht getrennt
 * in `rueckstandVertraege`.
 */
export const auswertungAboMonatSchema = z
    .object({
    monat: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Erwartet JJJJ-MM'),
    anzahl: z.number().int().min(0),
    netto: centAmount,
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/abo` — der wiederkehrende Umsatz
 * dieses Systems.
 *
 * **`mrr` ist eine MOMENTAUFNAHME und keine Zeitraumgrösse** (`D-19`): die
 * Summe der auf den Monat normierten Nettobeträge aller **aktiven** Vorlagen,
 * zum Stichtag der Antwort. Sie ändert sich nicht, wenn man den
 * Zeitraumschalter bewegt — der Zeitraum betrifft ausschliesslich die
 * Vorschau in `monate` und die Abgänge.
 *
 * **Die Grenze steht in `grenzen` und nicht nur hier:** Gezählt wird der
 * wiederkehrende Umsatz **dieses Systems**; ein Vertrag, der noch in einem
 * Vorsystem fakturiert wird, steht als Entwurf da und zählt nicht mit. Ohne
 * diesen Satz liest der Leser eine Zahl, die zu klein ist, und sucht den
 * Fehler im System. Der Satz nennt kein Datum: Er geht an jeden Kunden.
 *
 * **Abgänge sind absolute Zahlen und keine Quote** (`D-18`) — dasselbe Feld
 * wie in der Kundenauswertung, dasselbe Schema, dieselbe Begründung.
 */
export const auswertungAboSchema = z
    .object({
    ...auswertungHuelleGestalt,
    mrr: centAmount,
    vertraege: z.array(auswertungAboVertragSchema),
    /** Die Vorschau je Kalendermonat des gewählten Zeitraums, ab heute. */
    monate: z.array(auswertungAboMonatSchema),
    /**
     * Aktive Verträge, deren Stichtag um mehr als eine volle Periode
     * zurückliegt — der Generator hat sie übersprungen. **Arbeit, kein
     * Verstoss**, und ausdrücklich NICHT in der Vorschau enthalten.
     */
    rueckstandVertraege: z.number().int().min(0),
    abgaenge: auswertungAboAbgaengeSchema,
})
    .strict();
// ===========================================================================
// PROGNOSE — Task 10 [AW-prognose]
// ===========================================================================
/** Ein Monat der Vorschau. `saisonfaktor` ist `null`, wenn ohne Saison gerechnet wurde. */
export const auswertungPrognosePunktSchema = z
    .object({
    monat: z.string().regex(/^\d{4}-\d{2}$/, 'Monat als YYYY-MM'),
    wert: centAmount,
    saisonfaktor: auswertungFaktorSchema.nullable(),
})
    .strict();
/**
 * Die rückwärts geprüfte Spannweite — **ausdrücklich kein Konfidenzband**
 * (`D-26`).
 *
 * Vier Jahresbeobachtungen je Kalendermonat, davon eine aus einem Anlaufjahr,
 * tragen keine Streuungsangabe. Was hier steht, ist gemessen: Für jeden
 * rückwärts prüfbaren Monat wurde das Verfahren mit dem Wissensstand von
 * damals neu gerechnet und gegen den tatsächlichen Wert gehalten. `von` und
 * `bis` sind die kleinste und die grösste relative Abweichung.
 *
 * **Das Feld heisst nirgends `band` und nirgends `konfidenz`.** Eine
 * Spannweite, die man Band nennt, wird als Band gelesen — und ein Band
 * behauptet eine Wahrscheinlichkeitsaussage, die dieses Verfahren nicht
 * trifft.
 */
export const auswertungPrognoseSpannweiteSchema = z
    .object({
    von: auswertungFaktorSchema,
    bis: auswertungFaktorSchema,
    /** Wie viele Monate wirklich rückwärts geprüft wurden. */
    geprueft: z.number().int().nonnegative(),
    /** Wie viele übergangen wurden, weil ihr Istwert exakt null war (`D-14`). */
    uebergangen: z.number().int().nonnegative(),
})
    .strict();
/** Ein Monat der Liquiditätsvorschau — nur die Einzahlungsseite (`D-27`). */
export const auswertungLiquiditaetMonatSchema = z
    .object({
    monat: z.string().regex(/^\d{4}-\d{2}$/, 'Monat als YYYY-MM'),
    /** Fällige offene Posten dieses Monats. Ältere Fälligkeiten stehen im ersten Monat. */
    zuflussOffenePosten: centAmount,
    /** Geldkontenstand am Ende dieses Monats, wenn jeder Posten pünktlich einginge. */
    bestandEnde: centAmount,
})
    .strict();
/**
 * Die Liquiditätsvorschau — **Einzahlungsseite allein** (`D-27`, `E-1175`).
 *
 * **Es gibt kein Feld für eine Auszahlung, und das ist die Entscheidung.**
 * Geplante wiederkehrende Aufwände sind in diesem System nirgends geführt —
 * keine Tabelle, kein Vertrag. Eine Vorschau, die sie unterstellte, wäre zu
 * optimistisch, und bei Liquidität ist das die gefährliche Richtung. Die
 * Lücke steht in den `grenzen` der Antwort und nicht nur in der Oberfläche:
 * Wer die Zahlen über die Schnittstelle abholt, muss sie genauso lesen wie
 * jemand, der auf die Seite schaut.
 */
export const auswertungLiquiditaetSchema = z
    .object({
    /** Der Journalsaldo der drei Geldkonten heute. */
    geldkontenstand: centAmount,
    monate: z.array(auswertungLiquiditaetMonatSchema),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/prognose`.
 *
 * **`moeglich === false` ist ein vollwertiger Zustand und kein Fehler**
 * (`D-15`, `E-1172`). Unter 24 abgeschlossenen Monaten gibt es keine Kurve,
 * sondern einen Satz mit der Zahl der vorhandenen Monate — dieselbe Regel wie
 * beim Wachstum gegen null, eine Ebene höher. `grund` trägt diesen Satz.
 *
 * **`methode` steht IM Bild und nicht in einer Legende** (`D-11`): „Trend der
 * letzten 24 abgeschlossenen Monate, Saisonfaktor je Kalendermonat aus *n*
 * abgeschlossenen Vorjahren. Eine Vorschau ersetzt keine Planung."
 *
 * **Keine Ergebnisprognose** (`AW-2`). Die Aufwandsseite beginnt 2026 und
 * kennt keine Planwerte; ein Feld dafür gäbe es hier auch dann nicht, wenn
 * jemand es füllen wollte.
 */
export const auswertungPrognoseSchema = z
    .object({
    ...auswertungHuelleGestalt,
    moeglich: z.boolean(),
    /** Der Satz, der die Kurve ersetzt. `null`, sobald es eine Kurve gibt. */
    grund: z.string().nullable(),
    abgeschlosseneMonate: z.number().int().nonnegative(),
    trendMonate: z.number().int().positive(),
    saisonVorjahre: z.number().int().nonnegative(),
    mitSaison: z.boolean(),
    methode: z.string().min(1),
    /** Die Istreihe, gegen die die Vorschau gezeichnet wird. */
    ist: z.array(auswertungUmsatzMonatSchema.pick({ monat: true }).extend({ netto: centAmount })),
    vorschau: z.array(auswertungPrognosePunktSchema),
    spannweite: auswertungPrognoseSpannweiteSchema.nullable(),
    liquiditaet: auswertungLiquiditaetSchema,
})
    .strict();
// ===========================================================================
// ZAHLUNGSWEGE UND GUTHABEN — Auftrag `AW-zahlwege`, Plan Task 6
// ===========================================================================
//
// **Zwei Quellen für dieselbe Frage, und der Vertrag hält sie auseinander**
// (`D-20`). Für 2026 kommt der Zahlungsweg aus dem Geldkonto der Buchung;
// für 2022 bis 2025 gibt es keine Buchung, und die einzige Quelle ist
// `document.legacy_pay_method`. Sie stehen deshalb in ZWEI Feldern —
// `gebucht` und `altbestand` — und nicht in einer Liste mit einer
// Quellspalte, die eine Oberfläche übersehen könnte.
//
// **Die Liste mit Quellspalte gibt es trotzdem, eine Ebene tiefer**: Die
// Sicht `auswertung_zahlweg_monat` (Migration `0207`) trägt sie je Zeile,
// weil eine zweite Abfrage den Bruch sonst stillschweigend glätten könnte.
// Der Vertrag verschärft, was die Sicht bereits garantiert.
// Ein Verhältnis dieses Moduls ist `auswertungFaktorSchema` weiter oben —
// dieselbe Bauform wie `dashboardTrendFactorSchema` in `berichte.ts`, und aus
// demselben Grund eine eigene Zeile statt eines Imports: Jener ist dort nicht
// exportiert. Negativ und über 1 sind erlaubt; ein Rückfluss-Anteil kann null
// sein, und ein Wachstum über 100 % kommt vor.
/**
 * Ein Anteil einer Serie: fachlicher Schlüssel, Beschriftung, Anzahl,
 * Betrag — und der Anteil am Ganzen als `Factor`.
 *
 * **`anteil` ist ein `Factor` und niemals eine `number`** (`D-3`). Eine
 * Prozentangabe ist eine Geldableitung und unterliegt Hausregel 1 genauso
 * wie ein Betrag selbst; `money/no-float-money` greift auch in Tests.
 *
 * **`null` und nicht `0`, wenn die Bezugsgrösse exakt null ist** (`D-14`):
 * Ein Anteil an nichts ist kein Wert, sondern ein Satz — und die Oberfläche
 * schreibt ihn.
 */
export const auswertungAnteilSchema = z
    .object({
    schluessel: z.string().min(1),
    name: z.string().min(1),
    anzahl: z.number().int().nonnegative(),
    betrag: centAmount,
    anteil: auswertungFaktorSchema.nullable(),
})
    .strict();
/**
 * Eine Monatsreihe: die Rubriken (Monatsbeschriftungen) und je Kategorie
 * eine Reihe gleicher Länge.
 *
 * **Gleich lang und nicht lückenhaft**: Ein Monat ohne Bewegung trägt
 * `"0.00"` und keine Lücke. Der Unterschied zwischen „null Euro" und „nicht
 * erhoben" ist eine Aussage der Abfrage und keine der Zeichnung — die
 * Grenzen sagen, ab wann die Reihe überhaupt etwas wissen kann.
 */
export const auswertungMonatsreiheSchema = z
    .object({
    rubriken: z.array(z.string().min(1)),
    reihen: z.array(z
        .object({
        schluessel: z.string().min(1),
        name: z.string().min(1),
        werte: z.array(centAmount),
    })
        .strict()),
})
    .strict();
/**
 * Ein Monat des Guthabenkontos — die **vollständige Zerlegung** seiner
 * Bewegung, nicht eine Auswahl daraus.
 *
 * `aufladung + einloesungRueck + sonstigeHaben` minus
 * `aufladungSoll + einloesung + sonstigeSoll` ergibt `veraenderung`, und
 * `bestandEnde` ist deren Fortschreibung. Eine Zerlegung mit einem Rest, den
 * niemand führt, wäre ein Bestand, der sich nicht erklären lässt.
 *
 * **`aufladungSoll` steht getrennt und wird nie gegen `aufladung`
 * saldiert** (`E-1159`). Von 1.563 Aufladungsbuchungen tragen 121 ein Soll
 * auf dem Guthabenkonto über zusammen 3.652,88 € (gemessen 2026-09-10). Ihre
 * Natur ist nicht aufgeklärt; eine Vermutung wird nicht weggerechnet.
 */
export const auswertungGuthabenMonatSchema = z
    .object({
    monat: isoDate,
    aufladungAnzahl: z.number().int().nonnegative(),
    aufladung: centAmount,
    aufladungSollAnzahl: z.number().int().nonnegative(),
    aufladungSoll: centAmount,
    einloesungAnzahl: z.number().int().nonnegative(),
    einloesung: centAmount,
    einloesungRueck: centAmount,
    sonstigeHaben: centAmount,
    sonstigeSoll: centAmount,
    veraenderung: centAmount,
    bestandEnde: centAmount,
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/zahlungswege`.
 *
 * **Die vier Wege der Journalschicht sind vier und nicht drei** (Spec 4.3):
 * die drei Geldkonten plus die Guthaben-Einlösung, die aus keinem von ihnen
 * kommt. Sie erscheint nie als Geldeingang — sie tilgt eine Forderung, ohne
 * dass Geld fliesst (`E-1157`).
 *
 * **`ungebuchteAuszahlungen` wird gezeigt und nicht weggelassen** (Spec 4.3,
 * letzte Zeile). Solange eine Auszahlung eines Zahldienstes an die Bank
 * ungebucht ist, ist der Journalsaldo eines Geldkontos **nicht** sein
 * Kontostand. Die Zahl kommt aus `zahldienst_auszahlung_ohne_buchung`
 * (Migration `0196`) und nicht aus einer zweiten Ableitung.
 */
export const auswertungZahlungswegeSchema = z
    .object({
    ...auswertungHuelleGestalt,
    gebucht: z
        .object({
        ab: isoDate,
        gesamt: centAmount,
        wege: z.array(auswertungAnteilSchema),
        reihe: auswertungMonatsreiheSchema,
    })
        .strict(),
    altbestand: z
        .object({
        bis: isoDate,
        gesamt: centAmount,
        wege: z.array(auswertungAnteilSchema),
        reihe: auswertungMonatsreiheSchema,
    })
        .strict(),
    rueckfluesse: z
        .object({
        gesamt: centAmount,
        wege: z.array(auswertungAnteilSchema),
    })
        .strict(),
    guthaben: z
        .object({
        bestand: centAmount,
        monate: z.array(auswertungGuthabenMonatSchema),
    })
        .strict(),
    ungebuchteAuszahlungen: z
        .object({
        anzahl: z.number().int().nonnegative(),
        betrag: centAmount,
    })
        .strict(),
})
    .strict();
// ===========================================================================
// FORDERUNGEN — Auftrag `AW-forderungen`, Plan Task 7
// ===========================================================================
//
// **Eine Altersstruktur und ein Mahnstand, der nicht dieser ist** (`D-29`).
// Es gibt in diesem System kein Mahnwesen: keinen Mahnbeleg, keinen Lauf,
// keine Stufe. Was es gibt, ist die Mahnangabe, die der Altimport an den
// Altbelegen mitgebracht hat — eine **Herkunftsangabe**, keine Kennzahl
// dieses Systems, und deshalb heisst das Feld `mahnstandAltbestand` und
// nicht `mahnwesen`.
//
// **Keine Quote aus den Klärfällen.** Ein Klärfall ist Arbeit und kein
// Verstoss; eine Fehlerkurve daraus erzeugt Druck ohne Aussage. Was
// geliefert wird, ist ein Bestand je Grund.
/**
 * Ein Altersband des offenen Bestands.
 *
 * `vonTage`/`bisTage` sind Tage **seit Fälligkeit**: `null` als `vonTage`
 * heisst „noch nicht fällig", `null` als `bisTage` „ohne obere Grenze". Das
 * Band ohne Fälligkeitsdatum trägt beide als `null` und ist an seinem
 * Schlüssel erkennbar — ein Beleg ohne `due_date` hat kein Alter, und ihn in
 * das jüngste Band zu legen wäre eine Behauptung über seine Fälligkeit.
 */
export const auswertungAltersbandSchema = z
    .object({
    schluessel: z.string().min(1),
    name: z.string().min(1),
    vonTage: z.number().int().nullable(),
    bisTage: z.number().int().nullable(),
    anzahl: z.number().int().nonnegative(),
    betrag: centAmount,
    anteil: auswertungFaktorSchema.nullable(),
})
    .strict();
/**
 * Die Forderungslaufzeit — offener Bestand gegen Umsatz des Zeitraums, in
 * Tagen.
 *
 * **`tage` ist ein `Factor` und keine `number`** (`D-3`). Ein Tag ist zwar
 * kein Betrag, aber diese Zahl entsteht aus zwei Beträgen: Sie wird dezimal
 * über `factor(mul(div(bestand, factor(umsatz)), factor(tageImZeitraum)))`
 * gerechnet, und ein `Number()` auf dem Weg dorthin wäre die erste
 * Fliesskomma-Geldableitung dieses Hauses.
 *
 * **`null` und nicht `0`, wenn der Zeitraum keinen Umsatz trägt** (`D-14`).
 * Eine Laufzeit ohne Bezugsgrösse ist kein Wert, sondern ein Satz — und die
 * Oberfläche schreibt ihn.
 *
 * **Zwei Zeitbasen in einer Zahl** (`D-12`): Der Bestand ist eine
 * Momentaufnahme zum Stichtag, der Umsatz die Summe eines Zeitraums. Die
 * Zahl ist eine Näherung, und die Grenzen der Antwort sagen das.
 */
export const auswertungLaufzeitSchema = z
    .object({
    tage: auswertungFaktorSchema.nullable(),
    umsatzBrutto: centAmount,
    tageImZeitraum: z.number().int().positive(),
})
    .strict();
/** Ein Klärgrund mit seinem Bestand — Arbeit, kein Verstoss. */
export const auswertungKlaergrundSchema = z
    .object({ grund: z.string().min(1), anzahl: z.number().int().nonnegative() })
    .strict();
/**
 * Eine Mahnstufe des ALTBESTANDS — die Angabe, die der Altimport mitgebracht
 * hat, nie eine Stufe dieses Systems (`D-29`).
 */
export const auswertungMahnstufeSchema = z
    .object({
    stufe: z.number().int().positive(),
    anzahl: z.number().int().nonnegative(),
    gebuehren: centAmount,
    zinsen: centAmount,
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/forderungen`.
 *
 * **Keine eigene Sicht** (`D-6`): Alles kommt aus `beleg_offener_rest`
 * (Migrationen `0161`/`0174`/`0181`), `zahlung_klaerung` (`0161`) und den
 * Mahnfeldern aus `0158`. Die Altersbänder sind eine Gruppierung im Dienst,
 * weil sie genau einen Leser haben.
 *
 * **Die Grenzen werden ÜBERNOMMEN und nicht neu geschrieben:**
 * `apps/api/src/berichte/offene-posten.query.ts` führt sie als benannte
 * Konstanten, und dieser Endpunkt importiert die, die für ihn gelten. Eine
 * abgeschriebene Grenze veraltet.
 */
export const auswertungForderungenSchema = z
    .object({
    ...auswertungHuelleGestalt,
    bestand: z
        .object({
        gesamt: centAmount,
        anzahl: z.number().int().nonnegative(),
        ueberfaellig: centAmount,
        ueberfaelligAnzahl: z.number().int().nonnegative(),
        ueberfaelligAnteil: auswertungFaktorSchema.nullable(),
        ueberzahltAnzahl: z.number().int().nonnegative(),
        ueberzahltBetrag: centAmount,
    })
        .strict(),
    altersbaender: z.array(auswertungAltersbandSchema),
    laufzeit: auswertungLaufzeitSchema,
    klaerfaelle: z
        .object({ gesamt: z.number().int().nonnegative(), gruende: z.array(auswertungKlaergrundSchema) })
        .strict(),
    mahnstandAltbestand: z
        .object({
        anzahl: z.number().int().nonnegative(),
        gebuehren: centAmount,
        zinsen: centAmount,
        stufen: z.array(auswertungMahnstufeSchema),
    })
        .strict(),
})
    .strict();
// --- Kosten und Ergebnis [AW-kosten] ---------------------------------------
//
// `GET /api/v1/auswertungen/kosten` — Spec Abschnitt 4.5, Plan Task 8,
// Entscheidungen `D-4`, `D-6`, `D-8`, `D-12`, `D-14`, `D-16`, `D-22`.
//
// **Diese Seite trägt die ANDERE Zeitbasis.** Aufwand zählt zum
// Buchungsdatum und beginnt 2026; Umsatz zählt zum Rechnungsdatum und reicht
// bis 2022. Beides steht hier nebeneinander, weil die Kostenquote und die
// Ergebnisvorschau ohne beides gar nicht entstehen — und genau deshalb
// nennt jede abgeleitete Grösse ihre gemischte Herkunft selbst (`D-12`).
//
// **Das Ergebnis wird nicht zum zweiten Mal definiert** (`D-8`, `D-22`). Es
// heisst `einnahmenAusgabenSaldo` wie im Dashboard-Vertrag, und es heisst
// seit dem 2026-08-31 so, damit niemand es für eine GuV hält: keine
// Abgrenzung, keine Rückstellung, kein Testat. Der Dienst rechnet es nicht
// selbst nach, er ruft dieselbe Ableitung (`E-1167`).
/**
 * Ein Aufwandskonto der Rangliste.
 *
 * `konto` ist die **Kontonummer** (`account.number`) und nicht die
 * versionierte `account.id` — dieselbe Wahl wie im Dashboard-Vertrag, und
 * aus demselben Grund: Eine Kennung zeigt auf EINE Kontoversion und zerlegte
 * das Konto am Tag seiner Fortschreibung in zwei Zeilen der Liste.
 *
 * `gruppe` ist die Anzeigegruppe aus dem Kontenkatalog, ersatzweise die
 * SKR03-Kontenklasse. Sie steht **an der Kontozeile** und nicht nur in der
 * Gruppenliste darunter: Wer eine einzelne Zeile liest oder ausschneidet,
 * soll nicht nachschlagen müssen, wohin sie gehört.
 */
export const auswertungAufwandKontoSchema = z
    .object({
    konto: z.string().min(1),
    name: z.string().min(1),
    gruppe: z.string().min(1),
    betrag: centAmount,
})
    .strict();
/**
 * Eine Aufwandsgruppe mit ihrer Summe.
 *
 * **Die Gruppen sind vollständig und werden nicht abgeschnitten.** Anders als
 * die Kontenliste, die nach `n` Zeilen einen benannten Rest führt: Es gibt
 * je Bestand eine Handvoll Gruppen, und eine abgeschnittene Gruppenliste
 * spart nichts und verliert die Summe.
 */
export const auswertungAufwandGruppeSchema = z
    .object({
    gruppe: z.string().min(1),
    betrag: centAmount,
    anzahlKonten: z.number().int().nonnegative(),
})
    .strict();
/**
 * Ein Monat der Zeitreihe unter dem Chart — Aufwand, Umsatz und ihr Saldo.
 *
 * **Zwei Zeitbasen in einer Zeile, und das ist bewusst** (`D-12`): `aufwand`
 * zählt zum Buchungsdatum, `umsatzNetto` zum Rechnungsdatum. Beide in EINE
 * Kurve zu legen wäre falsch; sie als zwei Reihen in EIN Bild zu legen ist
 * richtig, sobald die Trennlinie „Journalbeginn" darin steht.
 *
 * **Ohne Beschriftung.** Wie ein Monat heisst, entscheidet die Anzeige aus
 * `von`; ein deutscher Anzeigetext im Vertrag müsste mitversioniert werden.
 */
export const auswertungKostenMonatSchema = z
    .object({
    von: isoDate,
    bis: isoDate,
    aufwand: centAmount,
    umsatzNetto: centAmount,
    einnahmenAusgabenSaldo: centAmount,
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/kosten`.
 *
 * **`kostenquote` ist ein `Factor` und niemals eine `number`** (`D-3`). Sie
 * ist ein Bruch aus zwei Zeitrechnungen — Aufwand am Buchungsdatum gegen
 * Umsatz am Rechnungsdatum — und damit eine Näherung, die die Anzeige so
 * beschriftet. **Ohne Erlös im Zeitraum ist sie `null` und nicht `0`**
 * (`D-14`): Eine Quote ohne Bezugsgrösse behauptet nichts, das stimmt.
 *
 * **Das Schema benutzt die Faktorprüfung des Dashboard-Vertrags** und
 * schreibt keine zweite: Es ist derselbe Wertebereich (negativ möglich, über
 * `1` möglich), und zwei Prüfungen für dieselbe Grösse wären zwei
 * Auslegungen davon, was ein Verhältnis in diesem Haus ist.
 *
 * **`rest` und `restAnzahlKonten` sind Pflicht und keine Zutat** (`E-1166`):
 * Eine abgeschnittene Liste ohne benannten Rest ist eine Lüge über die
 * Summe. `konten` plus `rest` ergibt `aufwandGesamt`, auf den Cent.
 *
 * **`journalbeginn` steht in der ANTWORT und nicht in der Oberfläche.** Die
 * Trennlinie im Bild hängt an ihm; eine Oberfläche, die den 1. Januar 2026
 * selbst kennte, trüge eine zweite Fassung einer Grenze, die der
 * Cutover-Kalender führt (`JOURNALBEGINN`, `apps/api/src/cutover/kalender.ts`).
 */
export const auswertungKostenSchema = z
    .object({
    ...auswertungHuelleGestalt,
    aufwandGesamt: centAmount,
    umsatzNetto: centAmount,
    einnahmenAusgabenSaldo: centAmount,
    kostenquote: dashboardTrendFactorSchema.nullable(),
    konten: z.array(auswertungAufwandKontoSchema),
    rest: centAmount,
    restAnzahlKonten: z.number().int().nonnegative(),
    gruppen: z.array(auswertungAufwandGruppeSchema),
    reihe: z.array(auswertungKostenMonatSchema),
    journalbeginn: isoDate,
})
    .strict();
// --- Steuer [AW-steuer] -----------------------------------------------------
//
// `GET /api/v1/auswertungen/steuer` — Spec Abschnitt 4.6, Plan Task 9,
// Entscheidungen `D-7`, `D-9`, `D-21`.
//
// **Eine Vorschau, die nach oben irrt, und das sagt.** Die Vorsteuerseite
// dieses Bestandes ist nicht vollständig — Eingangsrechnungen gibt es erst ab
// 2026 und nicht lückenlos —, und deshalb ist eine Zahllast aus diesen Zahlen
// eher zu hoch als zu niedrig. Das ist die weniger gefährliche Richtung, und
// sie steht trotzdem da: **in der Antwort und nicht nur in der Oberfläche**
// (`E-1169`). Eine Grenze, die nur im JSX steht, verschwindet beim nächsten
// Umbau der Seite; eine im Vertrag nicht.
//
// **Ausdrücklich keine Umsatzsteuer-Voranmeldung.** Kein Formular, keine
// Kennzahlenzuordnung — die Kennzahlen der Voranmeldung stehen seit `UV-api`
// (2026-10-10) auf der eigenen Unterseite, Vertrag `ustva.ts`.
/**
 * Die drei Steuerarten, wie `auswertung_steuerart()` (Migration `0208`) sie
 * vergibt.
 *
 * **Sie sind nicht drei Konten, sondern drei Aussagen.**
 * `umsatzsteuer_nicht_faellig` ist die Ist-Versteuerung: Die Steuer entsteht
 * mit der Vereinnahmung, nicht mit der Ausstellung — wer diese Zahl als
 * Zahllast liest, liest sie falsch. `umsatzsteuer_faellig` ist die vereinnahmte
 * Steuer. `vorsteuer` ist die abziehbare Seite.
 */
export const auswertungSteuerartSchema = z.enum([
    'umsatzsteuer_nicht_faellig',
    'umsatzsteuer_faellig',
    'vorsteuer',
]);
/**
 * Ein Steuerkonto mit seinem Betrag im Zeitraum.
 *
 * `betrag` steht in der FACHLICHEN Richtung der Art: bei den beiden
 * Umsatzsteuerarten der Haben-Überhang (was geschuldet ist), bei der
 * Vorsteuer der Soll-Überhang (was abziehbar ist). Die Sicht selbst führt
 * ausschliesslich `soll - haben`; die Umkehrung geschieht benannt im Dienst,
 * damit sie an einer Stelle steht und nicht in jeder Abfrage neu.
 */
export const auswertungSteuerKontoSchema = z
    .object({
    konto: z.string().min(1),
    name: z.string().min(1),
    art: auswertungSteuerartSchema,
    betrag: centAmount,
})
    .strict();
/** Ein Monat der Steuerreihe. */
export const auswertungSteuerMonatSchema = z
    .object({
    von: isoDate,
    bis: isoDate,
    umsatzsteuerFaellig: centAmount,
    umsatzsteuerNichtFaellig: centAmount,
    vorsteuer: centAmount,
    /**
     * Die abgeleitete Vorsteuer der Übergangsquelle — dieselbe Menge wie
     * Quelle (c) der Kennzahl 66 auf dem Kennzahlblatt (`D-32`).
     */
    vorsteuerAusSchluesseln: centAmount,
    zahllastVorschau: centAmount,
})
    .strict();
// Der Name einer Szenariogruppe steht als `auswertungSzenariogruppeNameSchema`
// weiter oben, bei der Umsatzauswertung — dieselben drei Werte, eine Stelle.
/**
 * Eine Szenariogruppe mit Nettoumsatz, Belegzahl und Anteil.
 *
 * **`netto` ist die exakte Grösse, `anteil` die abgeleitete.** Die Summe der
 * `netto`-Werte ist auf den Cent der Nettoumsatz desselben Zeitraums
 * (`E-1170`) — dieselbe Ableitung, dieselben Zeilen, nur eine
 * `GROUP BY`-Klausel mehr. Der `anteil` entsteht daraus über
 * `factor(div(...))`; er ist ein `Factor` und niemals eine `number` (`D-3`),
 * und er ist **`null`, wenn der Nettoumsatz des Zeitraums exakt null war**
 * (`D-14`) — auch dann, wenn einzelne Gruppen Werte tragen, die sich
 * gegenseitig aufheben.
 *
 * **Die benannte Grenze des Anteils:** Er wird nicht gerundet, aber er ist
 * eine Division. Drei Gruppen zu gleichen Teilen ergeben dreimal
 * `0.3333…` und in der Summe nicht exakt `1`. Wer eine Zusicherung über
 * Vollständigkeit braucht, prüft die BETRÄGE — die sind exakt und deshalb die
 * Grundlage dieser Zeile.
 */
export const auswertungSzenariogruppeSchema = z
    .object({
    gruppe: auswertungSzenariogruppeNameSchema,
    netto: centAmount,
    belege: z.number().int().nonnegative(),
    anteil: dashboardTrendFactorSchema.nullable(),
})
    .strict();
/**
 * Die offenen Szenario-Befunde — eine **Auskunft, keine Aufforderung**.
 *
 * `steuer_szenario_befund` (Migration `0180`) führt jeden ausgestellten Beleg,
 * dessen gespeichertes Steuerszenario nicht zu der aus der Lage des Empfängers
 * abgeleiteten passt. Gemessen sind das über neunhundert Altbelege mit
 * zusammen mehreren tausend Euro deutscher Umsatzsteuer, die nach dieser Lage
 * vermutlich nicht anzusetzen war.
 *
 * **Ob daraus eine Berichtigung folgt, entscheidet der Auftraggeber** — die
 * Steuerlogik-Ära hat das bereits so festgelegt, und diese Seite ändert daran
 * nichts. Deshalb steht hier eine Zahl und kein Zustand, keine Frist und kein
 * Knopf.
 *
 * `archivklasse` und `gebuchteKlasse` sind getrennt ausgewiesen, weil sie
 * verschieden schwer wiegen: Was auf einem alten PDF steht, lässt sich nach
 * § 146 Abs. 4 AO nicht mehr ändern; ein Befund an einem gebuchten Beleg
 * dieses Systems ist dagegen ein Vorgang.
 */
export const auswertungSzenariobefundSchema = z
    .object({
    anzahl: z.number().int().nonnegative(),
    steuer: centAmount,
    archivklasse: z.number().int().nonnegative(),
    gebuchteKlasse: z.number().int().nonnegative(),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/steuer`.
 *
 * **`zahllastVorschau` ist `umsatzsteuerFaellig` minus `vorsteuer` minus
 * `vorsteuerAusSchluesseln`, und ausdrücklich NICHT
 * `umsatzsteuerNichtFaellig` minus irgendetwas.** Die nicht fällige
 * Umsatzsteuer ist der Ist-Versteuerung geschuldet: Sie entsteht mit der
 * Zahlung und nicht mit der Ausstellung. Sie steht als eigene Zahl daneben,
 * damit sie sichtbar ist — und getrennt, damit sie niemand als Zahllast liest.
 *
 * **`vorsteuerAusSchluesseln` (`D-32` der Voranmeldungs-Spec, seit 2026-10-10)**
 * ist die abgeleitete Vorsteuer der Übergangsquelle — Kontierungszeilen mit
 * Vorsteuerschlüssel ohne Steuerzeile —, gelesen aus derselben Funktion
 * `ustva_einzelposten()` wie Kennzahl 66 und nach demselben Ausschluss. Ist
 * die Übergangsquelle leer, ist sie `0.00`.
 *
 * **Eine Unvollständigkeit steht in `grenzen`:** Die Vorsteuerseite ist nicht
 * lückenlos (die Vorschau ist dadurch eher zu hoch). Die zweite, die hier bis
 * zum 2026-10-10 stand — die Umbuchung von „nicht fällig" auf „fällig" sei
 * nicht gebaut —, ist seit `ST-ustfaellig` (2026-09-10) überholt und
 * gestrichen.
 *
 * **Die Szenario-Anteile reichen bis 2022 — als einzige Zahl dieser Seite.**
 * Sie kommen aus der Belegschicht (`document.tax_scenario`), nicht aus dem
 * Journal; alles andere hier beginnt am `journalbeginn`.
 */
export const auswertungSteuerSchema = z
    .object({
    ...auswertungHuelleGestalt,
    umsatzsteuerFaellig: centAmount,
    umsatzsteuerNichtFaellig: centAmount,
    vorsteuer: centAmount,
    vorsteuerAusSchluesseln: centAmount,
    zahllastVorschau: centAmount,
    konten: z.array(auswertungSteuerKontoSchema),
    reihe: z.array(auswertungSteuerMonatSchema),
    szenarien: z.array(auswertungSzenariogruppeSchema),
    szenarienNettoGesamt: centAmount,
    befunde: auswertungSzenariobefundSchema,
    journalbeginn: isoDate,
})
    .strict();
// --- BWA [BWA-grund/BWA-api] -------------------------------------------------
//
// `GET /api/v1/auswertungen/bwa` — Spec
// `docs/superpowers/specs/2026-09-14-bwa-design.md` Abschnitt 5, Plan Task 1
// `[BWA-grund]` (dieser Vertrag) und Task 2 `[BWA-api]` (der Dienst, der ihn
// füllt). Entscheidungen `D-4`, `D-6`, `D-9`, `D-10`, `D-11`, `D-14`.
//
// **Ein eigener, GENESTETER Schlüssel `bwa` und keine gespreizte Hülle wie
// bei Umsatz/Kosten/Steuer.** Der Grund ist `zeitraum` aus der Hülle
// (`auswertungHuelleGestalt`): Sein `typ` ist eine der fünf Zeiträume dieser
// Ära (Woche · Monat · Quartal · Jahr · Gesamt) — die BWA kennt keinen davon,
// sie wählt Jahr UND Monat zugleich (`D-14`, „eine BWA ist eine
// Monatsrechnung"). Eine gespreizte Antwort mit eigenen `jahr`/`monat`-Feldern
// NEBEN der geerbten `zeitraum` behauptete zwei Zeiträume in einer Antwort;
// der eigene Schlüssel trennt sie sichtbar.
/** Die zwei Ausprägungen einer BWA-Zeile — Postenzeile oder Summenzeile (Spec Abschnitt 2). */
export const auswertungBwaZeilenArtSchema = z.enum(['posten', 'summe']);
/**
 * Die Herkunft einer Vorjahreszelle (`D-9`, `BWA-3`).
 *
 * **Für 2026 gibt es kein gebuchtes Vorjahr.** Nur die Zeile `1010`
 * (Umsatzerlöse) trägt für 2026 ein Vorjahr, und zwar aus der Belegschicht
 * nach Rechnungsdatum — markiert `belegschicht`, damit die Seite den Bruch
 * aus zwei Zeitrechnungen zeigen kann. Ab dem ersten Jahr, dessen Vorjahr
 * vollständig im Journal steht, ist die Quelle `journal`. `null` heisst: kein
 * Vorjahreswert für diese Zeile.
 */
export const auswertungBwaVorjahrQuelleSchema = z.enum(['journal', 'belegschicht']);
/** Ein Konto, das in eine BWA-Zeile fällt — nur Nummer und Name, kein Betrag: Der Betrag steht auf der Zeile. */
export const auswertungBwaKontoSchema = z
    .object({
    nummer: z.string().min(1),
    name: z.string().min(1),
})
    .strict();
/**
 * Eine Zeile der BWA — Postenzeile mit ihren Konten oder Summenzeile ohne.
 *
 * **`konten` ist leer bei Summenzeilen UND bei einer Postenzeile ohne Konto**
 * (`D-6`: Bestandsveränderungen und aktivierte Eigenleistungen — ein
 * Dienstleister hat sie nicht). Eine leere Liste ist hier kein Fehlerfall,
 * sondern der Ausdruck von `bwa_konto_zuordnung` gegen den heutigen
 * Kontenrahmen.
 *
 * **`anteilMonat`/`anteilKumuliert` sind `Factor` und `null` ohne
 * Gesamtleistung** (`D-11`, Grenze 6 der Spec) — nie `0`: Ein Anteil ohne
 * Bezugsgrösse behauptet nichts, das stimmt.
 *
 * **`vorjahr` und `abweichung` sind unabhängig voneinander nullbar.** Ein
 * `vorjahr` von `null` heisst: keine Vorjahreszahl für diese Zeile (`D-9`);
 * `vorjahrQuelle` ist dann ebenfalls `null`. `abweichung` folgt daraus, ist
 * aber ein eigenes Feld und keine im Client nachgerechnete Differenz —
 * Hausregel 1 gilt für jede Geldableitung, nicht nur für die erste.
 */
export const auswertungBwaZeileSchema = z
    .object({
    nummer: z.string().min(1),
    bezeichnung: z.string().min(1),
    art: auswertungBwaZeilenArtSchema,
    konten: z.array(auswertungBwaKontoSchema),
    monat: centAmount,
    anteilMonat: auswertungFaktorSchema.nullable(),
    kumuliert: centAmount,
    anteilKumuliert: auswertungFaktorSchema.nullable(),
    vorjahr: centAmount.nullable(),
    vorjahrQuelle: auswertungBwaVorjahrQuelleSchema.nullable(),
    abweichung: centAmount.nullable(),
})
    .strict();
/**
 * Eine der fünf Kennzahlen (`D-11`): Rohertragsquote, Materialquote,
 * Personalkostenquote, Gesamtkostenquote, Umsatzrentabilität.
 *
 * **`wert` ist `null` ohne Bezugsgrösse, `hinweis` trägt dann den Satz**
 * (Spec Abschnitt 4.4) — dieselbe Arbeitsteilung wie `grenzen` auf der
 * Hülle: ein fehlender Wert ist keine Lücke im Bild, sondern eine benannte
 * Aussage darüber, warum es keinen gibt.
 */
export const auswertungBwaKennzahlSchema = z
    .object({
    schluessel: z.string().min(1),
    bezeichnung: z.string().min(1),
    wert: auswertungFaktorSchema.nullable(),
    hinweis: z.string().nullable(),
})
    .strict();
/**
 * Ein Monat der Zeitreihe unter dem Kennzahlenblock — Gesamtleistung,
 * Gesamtkosten, Betriebsergebnis (Spec Abschnitt 4.4: „die Monatsreihe
 * Gesamtleistung / Gesamtkosten / Betriebsergebnis als Balken").
 *
 * Januar bis zum gewählten Monat desselben Jahres (`D-14`) — dieselbe
 * kumulierte Spanne wie die Spalte „Kumuliert" der Zeilen, nur monatsweise
 * statt als eine Summe.
 */
export const auswertungBwaMonatsreihePunktSchema = z
    .object({
    /** `YYYY-MM`. */
    monat: z.string().regex(/^\d{4}-\d{2}$/, 'Monat als YYYY-MM'),
    gesamtleistung: centAmount,
    gesamtkosten: centAmount,
    betriebsergebnis: centAmount,
})
    .strict();
/** Der Kern der BWA-Antwort — siehe den Kopfkommentar dieses Abschnitts zur Nestung unter `bwa`. */
export const auswertungBwaKernSchema = z
    .object({
    jahr: z.number().int(),
    monat: z.number().int().min(1).max(12),
    zeilen: z.array(auswertungBwaZeileSchema),
    kennzahlen: z.array(auswertungBwaKennzahlSchema),
    monatsreihe: z.array(auswertungBwaMonatsreihePunktSchema),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/auswertungen/bwa`.
 *
 * **`grenzen` (aus der Hülle) trägt die sechs Sätze aus Spec Abschnitt 8**,
 * darunter Grenze 5 — sie erscheint NUR, solange mindestens eine Zeile von
 * `bwa_konto_zuordnung` noch `quelle = 'datev-standard'` trägt (`E-1300`).
 * Die DATEV-Standardzuordnung SKR03 → BWA 01 ist die Entscheidung des
 * Auftraggebers vom 2026-09-14 (`D-17`); eine spätere Abweichung der Kanzlei
 * ginge als Datenänderung mit Quelle `'steuerberater-bwa'` ein, eine Prüfung
 * dagegen ist nicht mehr vorgesehen.
 */
export const auswertungBwaSchema = z
    .object({
    ...auswertungHuelleGestalt,
    bwa: auswertungBwaKernSchema,
})
    .strict();
/**
 * Die Abfrageparameter von `GET /api/v1/auswertungen/bwa` und
 * `GET /api/v1/auswertungen/bwa/pdf` (`D-14`).
 *
 * **`jahr` und `monat` nur gemeinsam** — eine BWA ist eine Monatsrechnung
 * (Jahr UND Monat zugleich, Spec Abschnitt 5), und ein einzeln übergebenes
 * Jahr ohne Monat (oder umgekehrt) liesse offen, welchen der beiden der
 * Aufrufer eigentlich meinte. Ohne beide Parameter gilt die Vorgabe aus
 * `BwaQuery.bericht()` (`E-1302`). Die Regel ist ein `refine()` und lässt sich
 * in JSON-Schema nicht ausdrücken; die OpenAPI-Beschreibung nennt sie deshalb
 * im Beschreibungstext der beiden Routen.
 *
 * **Warum hier und nicht im Controller (`E-1710`):** Der OpenAPI-Erzeuger löst
 * das Schema nur auf, wenn es aus `@fameacc/contracts`, aus einem relativ
 * importierten Modul oder als Export der Controller-Datei stammt. Ein
 * nicht exportiertes Schema liess die beiden Routen ohne `in: 'query'`-Parameter
 * in `docs/openapi/openapi.json` stehen.
 */
export const bwaAnfrageSchema = z
    .object({
    jahr: z.coerce.number().int().min(1000).max(9999).optional(),
    monat: z.coerce.number().int().min(1).max(12).optional(),
})
    .strict()
    .refine((wert) => (wert.jahr === undefined) === (wert.monat === undefined), {
    message: 'jahr und monat werden nur gemeinsam angegeben',
});
//# sourceMappingURL=auswertungen.js.map